import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TodayIcon from "@material-ui/icons/Today";
import DateRangeIcon from "@material-ui/icons/DateRange";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import EventIcon from "@material-ui/icons/Event";
import HistoryIcon from "@material-ui/icons/History";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ReceiptIcon from "@material-ui/icons/Receipt";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import moment from "moment-timezone";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  cobrosPath,
  companyTz,
  formatDateTime,
  inDatePeriod,
  matchesQuery,
  money,
  moneyCompact,
  monthLabel,
  readQuery,
  servicesPath,
} from "./helpers";
import { EmptyHint, FilterChips, InfoDialog, KpiTile, LoadingHint, NegocioPager, PageFrame, RelatedLinks, SearchField, StatusChip, tNegocio, useMobile, usePager } from "./shared";
import { useConfirm, useRecords } from "./Lists";
import { useStyles } from "./styles";
import { i18n } from "../../translate/i18n";
import BinanceCheckoutDialog from "./BinanceCheckoutDialog";
import { ProductMark } from "./AccountPeek";
import { productFilterLabel } from "./catalog";

const locale = () => (i18n.language || "es").slice(0, 2);

const isBinanceMethod = (method) => {
  if (!method) return false;
  if (String(method.provider || "").toLowerCase() === "binance_pay") return true;
  return /binance/i.test(`${method.name || ""} ${method.type || ""}`);
};

const checkoutOf = (sale) =>
  sale?.binanceCheckout ||
  (Array.isArray(sale?.binanceCheckouts) ? sale.binanceCheckouts[0] : null);

const sumPaid = (rows) =>
  rows.reduce((acc, s) => acc + (s.status === "paid" ? Number(s.total || 0) : 0), 0);

const serviceOf = (sale) => sale?.serviceSnapshot || "—";

const customerOf = (sale) => sale?.customerSnapshot || sale?.contact?.name || "—";

const productIdsOf = (sale) => {
  const fromSubs = (sale?.subscriptions || [])
    .map((row) => Number(row.productId || row.product?.id))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (fromSubs.length) return fromSubs;
  return [];
};

const movementType = (sale) => {
  if (Number(sale.total) < 0 || /canje/i.test(String(sale.notes || ""))) return "redeem";
  return "sale";
};

const downloadCsv = (rows) => {
  const header = ["id", "fecha", "tipo", "cliente", "servicio", "monto", "estado", "pago"];
  const lines = rows.map((s) =>
    [
      s.id,
      s.createdAt || "",
      movementType(s),
      customerOf(s),
      serviceOf(s),
      s.total || 0,
      s.status || "",
      s.paymentMethod?.name || "",
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",")
  );
  const blob = new Blob([`\uFEFF${header.join(",")}\n${lines.join("\n")}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "movimientos-negocio.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const inCustomRange = (value, from, to) => {
  if (!from && !to) return true;
  const d = moment(value);
  if (!d.isValid()) return false;
  if (from && d.isBefore(moment(from).startOf("day"))) return false;
  if (to && d.isAfter(moment(to).endOf("day"))) return false;
  return true;
};

export default function SalesPage() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMobile();
  const location = useLocation();
  const { records, loading, reload } = useRecords("sales");
  const products = useRecords("products").records || [];
  const productOptions = useMemo(
    () =>
      [...(products || [])].sort((a, b) =>
        String(productFilterLabel(a, tNegocio, money)).localeCompare(
          String(productFilterLabel(b, tNegocio, money)),
          undefined,
          { sensitivity: "base" }
        )
      ),
    [products]
  );
  const payments = useRecords("payment-methods").records || [];
  const { confirm, setConfirm, runConfirm } = useConfirm(reload);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [period, setPeriod] = useState("all");
  const [month, setMonth] = useState(() => moment.tz(companyTz()).month());
  const [year, setYear] = useState(() => moment.tz(companyTz()).year());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [product, setProduct] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [payment, setPayment] = useState(null);
  const [info, setInfo] = useState(null);
  const [peekId, setPeekId] = useState(null);
  const [binanceSale, setBinanceSale] = useState(null);
  const extras = useMemo(() => ({ month, year }), [month, year]);

  React.useEffect(() => {
    const params = readQuery(location.search);
    if (params.contactId) {
      setCustomer({
        id: Number(params.contactId),
        name: "",
      });
    }
    if (params.saleId) setPeekId(Number(params.saleId));
  }, [location.search]);

  React.useEffect(() => {
    if (!peekId || loading) return;
    const row = records.find((s) => Number(s.id) === Number(peekId));
    if (row) setInfo(row);
    setPeekId(null);
  }, [peekId, records, loading]);

  const incomeRecords = useMemo(
    () => (records || []).filter((s) => s.status === "paid"),
    [records]
  );

  const customerOptions = useMemo(() => {
    const map = new Map();
    (records || []).forEach((s) => {
      const id = Number(s.contactId || s.contact?.id);
      if (!id || map.has(id)) return;
      map.set(id, { id, name: customerOf(s), number: s.contact?.number || "" });
    });
    return [...map.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [records]);

  React.useEffect(() => {
    if (!customer?.id || customer.name) return;
    const match = customerOptions.find((c) => Number(c.id) === Number(customer.id));
    if (match) setCustomer(match);
  }, [customer, customerOptions]);

  const years = useMemo(() => {
    const current = moment.tz(companyTz()).year();
    const min = (records || []).reduce((acc, s) => {
      const y = moment.tz(s.createdAt, companyTz()).year();
      return y && y < acc ? y : acc;
    }, current);
    const out = [];
    for (let y = min; y <= current; y += 1) out.push(y);
    return out;
  }, [records]);

  const matchesType = (s) => {
    if (!status) return true;
    if (status === "pending_payment") return s.status === "pending_payment";
    if (status === "redeem") return movementType(s) === "redeem";
    if (status === "sale") return s.status === "paid" && movementType(s) === "sale";
    return true;
  };
  const matchesSearch = (s) =>
    matchesQuery(s, query, [customerOf(s), s.contact?.number, s.paymentMethod?.name, s.id, serviceOf(s)]);
  const matchesProduct = (s) => {
    if (!product) return true;
    if (product.id) {
      const ids = productIdsOf(s);
      if (ids.length) return ids.includes(Number(product.id));
    }
    const needle = String(product.name || "").trim().toLowerCase();
    if (!needle) return true;
    return String(serviceOf(s)).toLowerCase().includes(needle);
  };
  const matchesCustomer = (s) =>
    !customer?.id || Number(s.contactId || s.contact?.id) === Number(customer.id);
  const matchesPayment = (s) =>
    !payment?.id || Number(s.paymentMethodId || s.paymentMethod?.id) === Number(payment.id);

  const typeScoped = useMemo(
    () => (records || []).filter((s) => matchesType(s) && matchesSearch(s)),
    [records, status, query]
  );
  const periodScoped = useMemo(
    () =>
      typeScoped.filter(
        (s) =>
          inDatePeriod(s.createdAt, period, extras) &&
          inCustomRange(s.createdAt, dateFrom, dateTo)
      ),
    [typeScoped, period, extras, dateFrom, dateTo]
  );
  const productScoped = useMemo(
    () => periodScoped.filter((s) => matchesProduct(s) && matchesPayment(s)),
    [periodScoped, product, payment]
  );

  const rows = useMemo(
    () => productScoped.filter((s) => matchesCustomer(s)),
    [productScoped, customer]
  );

  const filteredPaid = useMemo(() => rows.filter((s) => s.status === "paid"), [rows]);
  const filteredPending = useMemo(
    () => rows.filter((s) => s.status === "pending_payment"),
    [rows]
  );

  const kpis = useMemo(
    () => [
      { key: "today", value: (isMobile ? moneyCompact : money)(sumPaid(incomeRecords.filter((s) => inDatePeriod(s.createdAt, "today")))) },
      { key: "week", value: (isMobile ? moneyCompact : money)(sumPaid(incomeRecords.filter((s) => inDatePeriod(s.createdAt, "week")))) },
      { key: "month", value: (isMobile ? moneyCompact : money)(sumPaid(incomeRecords.filter((s) => inDatePeriod(s.createdAt, "month", extras)))) },
      { key: "year", value: (isMobile ? moneyCompact : money)(sumPaid(incomeRecords.filter((s) => inDatePeriod(s.createdAt, "year", extras)))) },
      { key: "historical", value: (isMobile ? moneyCompact : money)(sumPaid(incomeRecords)) },
    ],
    [incomeRecords, extras, isMobile]
  );

  const filterKpis = useMemo(() => {
    const income = sumPaid(filteredPaid);
    const pending = filteredPending.reduce((acc, s) => acc + Number(s.total || 0), 0);
    const avg = filteredPaid.length ? income / filteredPaid.length : 0;
    return [
      { key: "filterIncome", value: (isMobile ? moneyCompact : money)(income) },
      { key: "filterPending", value: (isMobile ? moneyCompact : money)(pending) },
      { key: "filterCount", value: String(rows.length) },
      { key: "filterAvg", value: (isMobile ? moneyCompact : money)(avg) },
    ];
  }, [filteredPaid, filteredPending, rows, isMobile]);

  const pager = usePager(rows);

  const yearSeries = useMemo(() => {
    const tz = companyTz();
    const now = moment.tz(tz);
    const yearNow = now.year();
    const lastMonth = now.month();
    const series = Array.from({ length: lastMonth + 1 }, (_, monthIndex) => ({
      month: monthIndex,
      year: yearNow,
      label: monthLabel(monthIndex, locale()),
      total: 0,
    }));
    incomeRecords.forEach((sale) => {
      const date = moment.tz(sale.createdAt, tz);
      if (!date.isValid() || date.year() !== yearNow || date.month() > lastMonth) return;
      series[date.month()].total += Number(sale.total || 0);
    });
    return series;
  }, [incomeRecords]);

  const yearTotal = yearSeries.reduce((acc, row) => acc + row.total, 0);
  const bestYearMonth = yearSeries.reduce((acc, row) => (!acc || row.total > acc.total ? row : acc), null);
  const topLimit = isMobile ? 4 : 6;

  const productBars = useMemo(() => {
    const map = new Map();
    filteredPaid.forEach((sale) => {
      const name = serviceOf(sale);
      const current = map.get(name) || { name, value: 0, count: 0 };
      current.value += Number(sale.total || 0);
      current.count += 1;
      map.set(name, current);
    });
    return [...map.values()].sort((a, b) => b.value - a.value).slice(0, topLimit);
  }, [filteredPaid, topLimit]);

  const clientBars = useMemo(() => {
    const map = new Map();
    filteredPaid.forEach((sale) => {
      const id = Number(sale.contactId || sale.contact?.id) || 0;
      const name = customerOf(sale);
      const key = id || name;
      const current = map.get(key) || { id, name, value: 0 };
      current.value += Number(sale.total || 0);
      map.set(key, current);
    });
    return [...map.values()].sort((a, b) => b.value - a.value).slice(0, topLimit);
  }, [filteredPaid, topLimit]);

  const paymentBars = useMemo(() => {
    const map = new Map();
    filteredPaid.forEach((sale) => {
      const name = sale.paymentMethod?.name || tNegocio("fields.none");
      const id = Number(sale.paymentMethodId || sale.paymentMethod?.id) || 0;
      const key = id || name;
      const current = map.get(key) || { id, name, value: 0 };
      current.value += Number(sale.total || 0);
      map.set(key, current);
    });
    return [...map.values()].sort((a, b) => b.value - a.value).slice(0, topLimit);
  }, [filteredPaid, topLimit]);

  const productMax = Math.max(1, ...productBars.map((row) => row.value));
  const clientMax = Math.max(1, ...clientBars.map((row) => row.value));
  const paymentMax = Math.max(1, ...paymentBars.map((row) => row.value));

  return (
    <>
      <PageFrame
        title={tNegocio("pages.sales.title")}
        subtitle={tNegocio("pages.sales.subtitle")}
        actions={
          <Button variant="outlined" onClick={() => downloadCsv(rows)}>
            {tNegocio("pages.sales.export")}
          </Button>
        }
      >
        <div className={classes.kpiGrid}>
          {kpis.map((k) => {
            const icon = { today: TodayIcon, week: DateRangeIcon, month: CalendarTodayIcon, year: EventIcon, historical: HistoryIcon }[k.key];
            return (
              <KpiTile
                key={k.key}
                icon={icon}
                label={tNegocio(`pages.sales.kpis.${k.key}`)}
                value={loading ? "…" : k.value}
                onClick={() => setPeriod(["today", "week", "month", "year"].includes(k.key) ? k.key : "all")}
              />
            );
          })}
        </div>
        <div className={classes.kpiGrid}>
          {filterKpis.map((k) => {
            const icon = { filterIncome: TrendingUpIcon, filterPending: HourglassEmptyIcon, filterCount: ReceiptIcon, filterAvg: EqualizerIcon }[k.key];
            const tone = { filterIncome: "kpiToneGood", filterPending: "kpiToneWarn" }[k.key];
            return (
              <KpiTile
                key={k.key}
                icon={icon}
                tone={tone}
                label={tNegocio(`pages.sales.kpis.${k.key}`)}
                value={loading ? "…" : k.value}
              />
            );
          })}
        </div>
        <div className={classes.toolbar}>
          <div className={classes.filterBar}>
            <FilterChips
              value={status}
              onChange={setStatus}
              options={[
                { value: "", label: tNegocio("filters.all") },
                { value: "sale", label: tNegocio("pages.sales.types.sale") },
                { value: "pending_payment", label: tNegocio("pages.sales.types.pending_payment") },
                { value: "redeem", label: tNegocio("pages.sales.types.redeem") },
              ]}
            />
            <span className={classes.filterSplit} />
            <FilterChips
              value={period}
              onChange={setPeriod}
              options={[
                { value: "all", label: tNegocio("pages.sales.periods.all") },
                { value: "today", label: tNegocio("pages.sales.periods.today") },
                { value: "week", label: tNegocio("pages.sales.periods.week") },
                { value: "month", label: tNegocio("pages.sales.periods.month") },
                { value: "year", label: tNegocio("pages.sales.periods.year") },
              ]}
            />
            {period === "month" || period === "year" ? (
              <>
                {period === "month" ? (
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    label={tNegocio("pages.sales.pickMonth")}
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    style={{ minWidth: 110 }}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i} value={i}>{monthLabel(i, locale())}</MenuItem>
                    ))}
                  </TextField>
                ) : null}
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label={tNegocio("pages.sales.pickYear")}
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  style={{ minWidth: 100 }}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}
          </div>
          <div className={classes.selectRow}>
            <TextField
              size="small"
              type="date"
              variant="outlined"
              label={tNegocio("pages.sales.dateFrom")}
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              type="date"
              variant="outlined"
              label={tNegocio("pages.sales.dateTo")}
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ minWidth: 150 }}
            />
            <Autocomplete
              size="small"
              options={productOptions}
              value={product}
              onChange={(_, value) => setProduct(value)}
              getOptionLabel={(o) => productFilterLabel(o, tNegocio, money) || o?.name || ""}
              getOptionSelected={(o, v) => Number(o?.id) === Number(v?.id)}
              filterOptions={(opts, state) => {
                const q = String(state.inputValue || "").trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((o) =>
                  String(productFilterLabel(o, tNegocio, money) || o?.name || "")
                    .toLowerCase()
                    .includes(q)
                );
              }}
              renderOption={(o) => (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <ProductMark product={o} size={22} circle link={false} />
                  <span>{productFilterLabel(o, tNegocio, money)}</span>
                </span>
              )}
              style={{ minWidth: 240 }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label={tNegocio("pages.sales.filterProduct")} />
              )}
            />
            <Autocomplete
              size="small"
              options={customerOptions}
              value={customer}
              onChange={(_, value) => setCustomer(value)}
              getOptionLabel={(o) => o?.name || ""}
              getOptionSelected={(o, v) => Number(o?.id) === Number(v?.id)}
              style={{ minWidth: 180 }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label={tNegocio("pages.sales.filterCustomer")} />
              )}
            />
            <Autocomplete
              size="small"
              options={payments}
              value={payment}
              onChange={(_, value) => setPayment(value)}
              getOptionLabel={(o) => o?.name || ""}
              getOptionSelected={(o, v) => Number(o?.id) === Number(v?.id)}
              style={{ minWidth: 160 }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label={tNegocio("pages.sales.filterPayment")} />
              )}
            />
            {isMobile ? null : (
              <SearchField value={query} onChange={setQuery} placeholder={tNegocio("pages.sales.search")} />
            )}
          </div>
          {isMobile ? (
            <SearchField value={query} onChange={setQuery} placeholder={tNegocio("pages.sales.search")} />
          ) : null}
        </div>
        <div className={classes.chartGrid}>
          <div className={classes.chartCard}>
            <div className={classes.sectionTitle} style={{ marginTop: 0 }}>
              {tNegocio("pages.dashboard.profitByMonth")} · {moment.tz(companyTz()).year()}
            </div>
            <div className={classes.chartMeta}>
              <div className={classes.chartMetaItem}>
                <div className={classes.chartMetaLabel}>{tNegocio("pages.dashboard.rangeTotal")}</div>
                <div>{isMobile ? moneyCompact(yearTotal) : money(yearTotal)}</div>
              </div>
              <div className={classes.chartMetaItem}>
                <div className={classes.chartMetaLabel}>{tNegocio("pages.dashboard.bestMonth")}</div>
                <div>
                  {bestYearMonth && bestYearMonth.total
                    ? `${bestYearMonth.label} · ${isMobile ? moneyCompact(bestYearMonth.total) : money(bestYearMonth.total)}`
                    : "—"}
                </div>
              </div>
            </div>
            <div style={{ width: "100%", height: isMobile ? 180 : 220 }}>
              <ResponsiveContainer>
                <AreaChart data={yearSeries} margin={{ top: 8, right: 8, left: isMobile ? 0 : 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="movementsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" stroke={theme.palette.text.secondary} tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <YAxis
                    width={isMobile ? 36 : 48}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    tickFormatter={(value) =>
                      Number(value).toLocaleString("es-CO", { notation: "compact", maximumFractionDigits: 1 })
                    }
                  />
                  <Tooltip formatter={(value) => money(value)} />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    fill="url(#movementsFill)"
                    isAnimationActive
                    animationDuration={900}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className={classes.chartCard}>
            <div className={classes.sectionTitle} style={{ marginTop: 0 }}>{tNegocio("pages.sales.chartByProduct")}</div>
            {productBars.length ? productBars.map((row) => (
              <div
                key={row.name}
                className={`${classes.chartRow} ${product?.name === row.name ? classes.chartRowOn : ""}`}
                onClick={() =>
                  setProduct(
                    product?.id && product?.name === row.name
                      ? null
                      : products.find((item) => item.name === row.name) || { name: row.name }
                  )
                }
                role="button"
                tabIndex={0}
              >
                <div className={classes.serviceName} title={row.name}>{row.name}</div>
                <div className={classes.serviceTrack}>
                  <div className={classes.serviceFill} style={{ width: `${(row.value / productMax) * 100}%` }} />
                </div>
                <div className={classes.serviceAmount}>{isMobile ? moneyCompact(row.value) : money(row.value)}</div>
              </div>
            )) : (
              <EmptyHint title={tNegocio("pages.sales.emptyTitle")} subtitle={tNegocio("pages.sales.empty")} />
            )}
          </div>
          <div className={`${classes.chartCard} ${classes.chartWide}`}>
            <div className={classes.sectionTitle} style={{ marginTop: 0 }}>{tNegocio("pages.sales.chartByCustomer")}</div>
            {clientBars.length ? clientBars.map((row) => (
              <div
                key={row.id || row.name}
                className={`${classes.chartRow} ${Number(customer?.id) === Number(row.id) ? classes.chartRowOn : ""}`}
                onClick={() => setCustomer(Number(customer?.id) === Number(row.id) ? null : { id: row.id, name: row.name })}
                role="button"
                tabIndex={0}
              >
                <div className={classes.serviceName} title={row.name}>{row.name}</div>
                <div className={classes.serviceTrack}>
                  <div className={classes.serviceFill} style={{ width: `${(row.value / clientMax) * 100}%` }} />
                </div>
                <div className={classes.serviceAmount}>{isMobile ? moneyCompact(row.value) : money(row.value)}</div>
              </div>
            )) : (
              <EmptyHint title={tNegocio("pages.sales.emptyTitle")} subtitle={tNegocio("pages.sales.empty")} />
            )}
          </div>
          <div className={classes.chartCard}>
            <div className={classes.sectionTitle} style={{ marginTop: 0 }}>{tNegocio("pages.sales.chartByPayment")}</div>
            {paymentBars.length ? paymentBars.map((row) => (
              <div
                key={row.id || row.name}
                className={`${classes.chartRow} ${Number(payment?.id) === Number(row.id) ? classes.chartRowOn : ""}`}
                onClick={() =>
                  setPayment(
                    Number(payment?.id) === Number(row.id)
                      ? null
                      : (payments.find((p) => Number(p.id) === Number(row.id)) || { id: row.id, name: row.name })
                  )
                }
                role="button"
                tabIndex={0}
              >
                <div className={classes.serviceName} title={row.name}>{row.name}</div>
                <div className={classes.serviceTrack}>
                  <div className={classes.serviceFill} style={{ width: `${(row.value / paymentMax) * 100}%` }} />
                </div>
                <div className={classes.serviceAmount}>{isMobile ? moneyCompact(row.value) : money(row.value)}</div>
              </div>
            )) : (
              <EmptyHint title={tNegocio("pages.sales.emptyTitle")} subtitle={tNegocio("pages.sales.empty")} />
            )}
          </div>
        </div>
        {rows.length ? (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{tNegocio("cols.date")}</TableCell>
                  <TableCell>{tNegocio("cols.type")}</TableCell>
                  <TableCell>{tNegocio("cols.contact")}</TableCell>
                  <TableCell>{tNegocio("cols.service")}</TableCell>
                  <TableCell>{tNegocio("cols.payment")}</TableCell>
                  <TableCell>{tNegocio("cols.total")}</TableCell>
                  <TableCell>{tNegocio("cols.status")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.slice.map((s, index) => (
                  <TableRow key={s.id} hover className={classes.clickableRow} onClick={() => setInfo(s)}>
                    <TableCell>{pager.offset + index + 1}</TableCell>
                    <TableCell>{formatDateTime(s.createdAt)}</TableCell>
                    <TableCell>
                      <StatusChip
                        value={movementType(s)}
                        prefix="negocioModule.pages.sales.types."
                      />
                    </TableCell>
                    <TableCell>
                      {customerOf(s)}
                      {s.contact?.number ? (
                        <div className={classes.captionBlock}>{s.contact.number}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>{serviceOf(s)}</TableCell>
                    <TableCell>{s.paymentMethod?.name || "—"}</TableCell>
                    <TableCell>{money(s.total)}</TableCell>
                    <TableCell>
                      <StatusChip value={s.status} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {s.status === "pending_payment" && (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => setConfirm({ action: "saleStatus", id: s.id, status: "paid" })}
                          >
                            {tNegocio("markPaid")}
                          </Button>
                          {(checkoutOf(s) || isBinanceMethod(s.paymentMethod)) && (
                            <Button
                              size="small"
                              onClick={() => setBinanceSale(s)}
                              style={{ marginLeft: 4 }}
                            >
                              {tNegocio("pages.binance.openCheckout")}
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <NegocioPager
              count={pager.count}
              page={pager.page}
              rowsPerPage={pager.rowsPerPage}
              onChangePage={pager.setPage}
              onChangeRowsPerPage={pager.setRowsPerPage}
            />
          </div>
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint
            title={tNegocio("pages.sales.emptyTitle")}
            subtitle={tNegocio("pages.sales.empty")}
          />
        )}
      </PageFrame>
      <InfoDialog
        open={!!info}
        onClose={() => setInfo(null)}
        title={customerOf(info) || tNegocio("pages.sales.title")}
        subtitle={info ? `#${info.id} · ${formatDateTime(info.createdAt)}` : ""}
        chips={
          info ? (
            <>
              <StatusChip value={movementType(info)} prefix="negocioModule.pages.sales.types." />
              <StatusChip value={info.status} />
            </>
          ) : null
        }
        fields={[
          { label: tNegocio("cols.contact"), value: customerOf(info) },
          { label: tNegocio("cols.phone"), value: info?.contact?.number },
          { label: tNegocio("cols.service"), value: info ? serviceOf(info) : "" },
          { label: tNegocio("cols.payment"), value: info?.paymentMethod?.name },
          { label: tNegocio("cols.total"), value: money(info?.total) },
          { label: tNegocio("fields.notes"), value: info?.notes },
        ]}
        extra={
          <RelatedLinks
            items={[
              info?.contact?.id || info?.contactId
                ? { to: `/negocio/clientes?contactId=${info.contact?.id || info.contactId}`, label: tNegocio("related.customers") }
                : null,
              info?.contact?.id || info?.contactId
                ? { to: servicesPath(info.contact?.id || info.contactId), label: tNegocio("related.services") }
                : null,
              info?.contact?.id || info?.contactId
                ? { to: cobrosPath(info.contact?.id || info.contactId), label: tNegocio("related.collections") }
                : null,
            ]}
          />
        }
      />
      <ConfirmationModal title={tNegocio("confirmTitle")} open={!!confirm} onClose={() => setConfirm(null)} onConfirm={runConfirm}>
        {tNegocio("confirmBody")}
      </ConfirmationModal>
      <BinanceCheckoutDialog
        open={!!binanceSale}
        saleId={binanceSale?.id}
        checkout={checkoutOf(binanceSale)}
        onClose={() => setBinanceSale(null)}
        onPaid={() => {
          setBinanceSale(null);
          reload();
        }}
      />
    </>
  );
}
